import { useState } from 'react';
import { Project, MediaType } from '../../../types';
import { supabase } from '../../../lib/supabase';

export function useProjectMedia(project: Project, setEditedProject: (project: Project) => void) {
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (!isVideo && !isImage) {
        setError(`File "${file.name}" is not a supported format`);
        continue;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${fileName}`;

      try {
        setUploadProgress(prev => ({ ...prev, [fileName]: 0 }));

        const { data, error: uploadError } = await supabase.storage
          .from('project-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
            onUploadProgress: (progress) => {
              const percent = (progress.loaded / progress.total) * 100;
              setUploadProgress(prev => ({ ...prev, [fileName]: percent }));
            },
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('project-images')
          .getPublicUrl(filePath);

        const mediaType: MediaType = isVideo ? 'video' : 'image';

        setEditedProject({
          ...project,
          images: [
            ...project.images,
            {
              id: crypto.randomUUID(),
              url: isImage ? publicUrl : '',
              video_url: isVideo ? publicUrl : undefined,
              media_type: mediaType,
              display_order: project.images.length,
              created_at: new Date().toISOString()
            }
          ]
        });

        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileName];
          return newProgress;
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        setError(`Failed to upload "${file.name}"`);
      }
    }
  };

  const handleYouTubeAdd = (embedUrl: string) => {
    setEditedProject({
      ...project,
      images: [
        ...project.images,
        {
          id: crypto.randomUUID(),
          url: embedUrl,
          media_type: 'youtube',
          display_order: project.images.length,
          created_at: new Date().toISOString()
        }
      ]
    });
  };

  const removeImage = async (imageToRemove: any) => {
    try {
      if (imageToRemove.media_type !== 'youtube') {
        const url = new URL(imageToRemove.media_type === 'video' ? imageToRemove.video_url! : imageToRemove.url);
        const filePath = url.pathname.split('/').pop();

        if (filePath) {
          const { error: deleteError } = await supabase.storage
            .from('project-images')
            .remove([filePath]);

          if (deleteError) throw deleteError;
        }
      }

      setEditedProject({
        ...project,
        images: project.images.filter(img => img.id !== imageToRemove.id)
      });
    } catch (error) {
      console.error('Error removing image:', error);
      setError('Failed to remove image');
    }
  };

  return {
    uploadProgress,
    error,
    handleFileUpload,
    handleYouTubeAdd,
    removeImage,
    setError
  };
}